import { Component, ElementRef, HostListener, Injector, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportProvider,
    CashFlowStatementPage,
    ICashFlowFLI,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCoreLayoutProvider, DateLocalePipe, FIRST_ELEMENT_INDEX, NumberUtils } from '@modeso/aumico-lib-core-fe';
import { TemplatesProvider, getTemplateById } from '@modeso/aumico-lib-templates-fe';
import {
    AnnualReportStep,
    FinancialStatementTypes,
    IAccountMappingTemplateGroupItemFLI,
    IAnnualReport,
    ICashFlowStatement,
    ICashFlowStatementEntry,
    IFinStatements,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemCashFlowType } from '@modeso/types__aumico-lib-settings-be';
import { ITemplateEntityDTO, TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, find, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { TotalCaptions } from '../../../../../../aumico-lib-annualreport-fe/src/lib/components/cash-flow-statement-components/cash-flow-total/cash-flow-total.component';
import { CFS } from '../../../../../../aumico-lib-annualreport-fe/src/lib/constants/autofill-options';
import {
    FCPState,
    GetOrganizationDto,
    OrganizationProvider,
} from '../../../../../../aumico-lib-users-fe/src/public-api';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../../environments/environment';
import { EqualityChecksResult } from '../../../enums/equality-checks-results.enum';
import { IStringDictionary } from '../../../interfaces/IStringDictionary.interface';
import { ConsistencyCheckResult } from '../../../interfaces/consistency-check-result';
import { DataState } from '../../../shared/enum/datastate.enum';
import { templateConstants } from '../../../shared/template-constants';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { CashFlowStatementHelper } from '../helpers/cash-flow-statement.helper';
import { EqualityChecksHelper } from '../helpers/equality-checks.helper';
import { CashFlowStatementMapper } from '../mappers/cash-flow-statement.mapper';
const debug = Debug('aumico:project-reports-fe:AnnualReportCSFPage');
@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-cash-flow-statement-page',
    templateUrl: 'cash-flow-statement.page.html',
    styleUrls: ['cash-flow-statement.page.scss'],
})
export class AnnualReportCSFPage extends AnnualReportSubPage implements OnInit {
    @ViewChild(CashFlowStatementPage, { static: false }) private cashFlowStatementRef: CashFlowStatementPage;
    currentLanguage = this.getCurrentLanguageWithFallBack();

    readonly defaultLanguage = environment.defaultLanguage;

    private readonly incomeIndex = 1;
    private readonly balanceIndex = 0;

    dataState: DataState = DataState.Loading;
    currentAnnualReport: IAnnualReport | undefined;
    private previousAnnualReportCashFlowStatement: ICashFlowStatement | undefined;
    // In case of edit mode
    currentAnnualReportCashFlowStatement: ICashFlowStatement | undefined;
    // data to be sent as input to cash flow statement
    profitOfTheYearFli: IAccountMappingTemplateGroupItemFLI | undefined;
    previousAnnualProfit: number | undefined;
    cashAndCashEquivalentsFli: IAccountMappingTemplateGroupItemFLI | undefined;

    balanceTemplate$: Observable<ITemplateEntityDTO> | Observable<undefined> = of(undefined);
    incomeTemplate$: Observable<ITemplateEntityDTO> | Observable<undefined> = of(undefined);

    previousOpeningCashAndCashEquivalents: number | null;
    previousClosingCashAndCashEquivalents: number | null;

    mappedFlis: ICashFlowFLI[] = [];

    currentYearDate: string;
    previousYearDate: string;
    currency: string;

    private balanceFlis: IAccountMappingTemplateGroupItemFLI[] = [];
    private incomeFlis: IAccountMappingTemplateGroupItemFLI[] = [];
    private flisCashFlowDictionary: IStringDictionary;

    private noPreviousDatePlaceholder = '-';

    autofillApplied: boolean;

    public currentCashFlowStatementTotal: number = 0;
    public previousCashFlowStatementTotal: number = 0;

    private isSaveRequestInProgress = false;

    isPartOfArNavigation$: Observable<boolean>;
    reportLocale: string;
    disableSaveButton: boolean;
    cashflowClosingDifference: number;
    cashflowOpeningDifference: number;
    isVersionMismatch: boolean;
    isSavedClicked: boolean;
    isFcpActive: boolean;
    totalCaptions: TotalCaptions;
    financialStatment: IFinStatements[];
    cashflowFromUploadAutofill: ICashFlowStatement;
    updateFlisAutomatically: boolean;

    operatingActivitiesTitle: string | undefined;
    financingActivitiesTitle: string | undefined;
    investingActivitiesTitle: string | undefined;

    private resizeObserver: ResizeObserver | undefined;

    @HostListener('window:resize')
    onResize(): void {
        this.updateColumnHeight();
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.resizeObserver = new ResizeObserver(() => this.updateColumnHeight());
        this.resizeObserver.observe(this.el.nativeElement);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    constructor(
        private annualReportProvider: AnnualReportProvider,
        private readonly organizationProvider: OrganizationProvider,
        private cashFlowStatementMapper: CashFlowStatementMapper,
        private templatesProvider: TemplatesProvider,
        private dateLocalPipe: DateLocalePipe,
        injector: Injector,
        public override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override actionsListener$: ActionsSubject,
        private readonly mixpanelService: MixpanelService,
        dialog: MatDialog,
        private readonly el: ElementRef
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.CFS;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(AnnualReportStep.CFS);
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.CASH_FLOW_TIME_SPENT_TILL_COMPLETION);
        super.ngOnInit();

        // Getting active Annual Report
        this.dataState = DataState.Loading;
        this.annualReportProvider
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.annualReportProvider.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        const filterFunc = (
            autoFilledAnnualReport: IAnnualReport,
            autoFilledAnnualReportFinStatements: IFinStatements[],
        ) => {
            return (
                autoFilledAnnualReport != null &&
                autoFilledAnnualReport.generalInformation != null &&
                autoFilledAnnualReportFinStatements.length >= templateConstants.financialStatementMinimumLength &&
                autoFilledAnnualReportFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping
                    .template != null &&
                autoFilledAnnualReportFinStatements[templateConstants.incomeTemplate].templateAndAccountsMapping
                    .template != null
            );
        };

        // No take(1)/untilDestroyed - switchMap in main chain handles cancellation
        const previousAnnualReport$ = this.annualReportProvider.getAnnualReportForBalanceAutoFill$().pipe(
            withLatestFrom(this.annualReportProvider.getBalanceAutofillFinStatements$()),
            filter(([autoFilledAnnualReport, autoFilledAnnualReportFinStatements]: [IAnnualReport, IFinStatements[]]) =>
                filterFunc(autoFilledAnnualReport, autoFilledAnnualReportFinStatements),
            ),
        );

        // No take(1)=untilDestroyed - switchMap in main chain handles cancellation
        const uploadAutofillReport$ = this.annualReportProvider
            .getAnnualReportForUploadAndMappingAutoFill$()
            .pipe(withLatestFrom(this.annualReportProvider.getSelectedYearForUploadAndMappingAutofillFinStatements$()));

        this.annualReportProvider
            // REV-20899 save-side isolation: this whole-report object is persisted
            // verbatim on save, so it MUST be the RAW report. The bridged
            // finStatements would zero a v1 report's inherited legacy reclasses.
            // The DISPLAY finStatements (withLatestFrom below) stay reclass-aware,
            // so cashflow values still include reclassifications.
            .getActiveAnnualReport$()
            .pipe(
                // FIX #1: Prevent duplicate emissions of the same report
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
                withLatestFrom(
                    this.annualReportProvider.getActiveFinStatementsWithReclasses$(),
                    this.organizationProvider.getCurrentOrganization$(),
                ),
                // FIX: Prevent duplicate emissions after combining
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
                map(
                    ([activeAnnualReport, finStatements, organization]: [
                        IAnnualReport,
                        IFinStatements[],
                        GetOrganizationDto,
                    ]) => {
                        if (
                            activeAnnualReport != null &&
                            activeAnnualReport.generalInformation != null &&
                            finStatements.length >= templateConstants.financialStatementMinimumLength &&
                            finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template !=
                            null &&
                            finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template != null
                        ) {
                            return [activeAnnualReport, finStatements, organization];
                        } else {
                            // Return null, so that this state can be handled
                            return [null, finStatements, organization];
                        }
                    },
                ),
                tap(
                    ([activeAnnualReport, finStatements, organization]: [
                        IAnnualReport,
                        IFinStatements[],
                        GetOrganizationDto,
                    ]) => {
                        this.isFcpActive =
                            [FCPState.PAID, FCPState.TRIAL].includes(
                                organization?.fcpActivationDetails?.subscriptionState,
                            ) && activeAnnualReport?.fcp != null;
                        this.financialStatment = finStatements;
                        this.handleGetActiveAnnualReport(activeAnnualReport, finStatements);
                    },
                ),
                // FIX #2: switchMap cancels previous template requests when new report arrives
                switchMap(() =>
                    combineLatest([this.balanceTemplate$, this.incomeTemplate$]).pipe(
                        // FIX #3: take(1) HERE is correct - we want templates once per report emission
                        // When activeAnnualReport changes, switchMap cancels this and starts fresh
                        take(1),
                    ),
                ),
                tap(([balanceTemplate, incomeTemplate]: [ITemplateEntityDTO, ITemplateEntityDTO]) =>
                    this.handleTemplates(balanceTemplate, incomeTemplate),
                ),
                filter(() => this.currentAnnualReport?.cashFlowStatement == null),
                // FIX #2: switchMap cancels previous autofill when new report arrives
                switchMap(() => {
                    const uploadAutofill = this.currentAnnualReport?.templateReportAutofillOptions?.[CFS];
                    if (uploadAutofill != null) {
                        return uploadAutofillReport$.pipe(take(1)); // take(1) to get current value once
                    }
                    return of([null, null] as [IAnnualReport, IFinStatements[]]);
                }),
                tap(([uploadAutofillAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) => {
                    if (uploadAutofillAnnualReport != null) {
                        this.autofillFromMapUploadAnnualReport(uploadAutofillAnnualReport);
                    }
                }),
                filter(
                    ([uploadAutofillAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) =>
                        uploadAutofillAnnualReport?.cashFlowStatement == null &&
                        !this.currentAnnualReport?.templateReportAutofillOptions?.[CFS],
                ),
                // FIX #2: switchMap cancels previous lookup when new report arrives
                switchMap(() => previousAnnualReport$.pipe(take(1))), // take(1) to get current value once
                untilDestroyed(this),
            )
            .subscribe((previousAnnualReport: [IAnnualReport, IFinStatements[]]) => {
                this.handlePreviousAnnualReport(previousAnnualReport);
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());
    }
    private handleGetActiveAnnualReport(annualReport: IAnnualReport | null, finStatements: IFinStatements[]): void {
        if (annualReport === null) {
            this.dataState = DataState.Error;
            return;
        }
        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;
        this.disableSaveButton = false;

        this.currentAnnualReport = cloneDeep<IAnnualReport>(annualReport);
        this.prepareCashFlowStatementData(annualReport, finStatements);

        this.dataState = DataState.Success;
        this.reportLocale = annualReport.locale;

        if (annualReport.cashFlowStatement != null) {
            this.currentAnnualReportCashFlowStatement = annualReport.cashFlowStatement;
            this.checkVersionMismatch(this.isFcpActive);
        } else {
            this.isVersionMismatch = false;
        }
    }

    private autofillFromMapUploadAnnualReport(annualReport: IAnnualReport | null) {
        if (annualReport === null) {
            return;
        }

        const cashflowFromUploadAutofill = cloneDeep<typeof annualReport.cashFlowStatement>(
            annualReport.cashFlowStatement,
        );

        cashflowFromUploadAutofill.investingActivities = {
            ...cashflowFromUploadAutofill.investingActivities,
            entries: cashflowFromUploadAutofill.investingActivities.entries.map((value) => ({
                ...value,
                calculatedPreviousValue: null,
                calculatedCurrentValue: null,
            })),
        };

        cashflowFromUploadAutofill.operatingActivities = {
            ...cashflowFromUploadAutofill.operatingActivities,
            entries: cashflowFromUploadAutofill.operatingActivities.entries.map((value) => ({
                ...value,
                calculatedPreviousValue: null,
                calculatedCurrentValue: null,
            })),
        };

        cashflowFromUploadAutofill.financingActivities = {
            ...cashflowFromUploadAutofill.financingActivities,
            entries: cashflowFromUploadAutofill.financingActivities.entries.map((value) => ({
                ...value,
                calculatedPreviousValue: null,
                calculatedCurrentValue: null,
            })),
        };

        cashflowFromUploadAutofill.total = {
            ...cashflowFromUploadAutofill.total,
            previousClosingCashAndCashEquivalents: null,
            previousOpeningCashAndCashEquivalents: null,
            openingCashAndCashEquivalents: this.cashAndCashEquivalentsFli?.calculatedOpeningBalance ?? null,
            closingCashAndCashEquivalents: this.cashAndCashEquivalentsFli?.calculatedClosingBalance ?? null,
        };

        this.cashflowFromUploadAutofill = cashflowFromUploadAutofill;

        this.updateFlisAutomatically = true;
        this.autofillApplied = false;
    }

    private checkVersionMismatch(isFcpActive: boolean) {
        if (isFcpActive && this.currentAnnualReport.fcp != null) {
            this.isVersionMismatch =
                this.currentAnnualReport.fcp.version !== this.currentAnnualReportCashFlowStatement.version;
        } else {
            this.isVersionMismatch =
                this.currentAnnualReport.finStatements[this.balanceIndex].version !==
                this.currentAnnualReportCashFlowStatement.version;
        }
    }

    private prepareCashFlowStatementData(annualReport: IAnnualReport, finStatements: IFinStatements[]): void {
        if (annualReport.generalInformation == null) {
            throw new Error('General information is null');
        }
        // setting current and previous year dates
        this.currentYearDate =
            this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearFromDate).toString() +
            '-' +
            (annualReport.generalInformation.incomeCurrentYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearToDate).toString()
                : '');
        this.previousYearDate = annualReport.generalInformation.noPreviousData
            ? this.noPreviousDatePlaceholder
            : this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearFromDate).toString() +
            '-' +
            (annualReport.generalInformation.incomePreviousYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearToDate).toString()
                : '');

        // setting currency
        this.currency = annualReport.generalInformation.currency?.name;

        // setting Flis
        this.prepareFlisFromSavedTemplate(finStatements);
        this.dispatchTaxonomies(annualReport);
    }

    private prepareFlisFromSavedTemplate(finStatements: IFinStatements[]): void {
        const BalanceTemplate = finStatements[this.balanceIndex].templateAndAccountsMapping.template;
        const IncomeTemplate = finStatements[this.incomeIndex].templateAndAccountsMapping.template;
        this.balanceFlis = this.cashFlowStatementMapper.getTemplateFlis(BalanceTemplate);
        this.incomeFlis = this.cashFlowStatementMapper.getTemplateFlis(IncomeTemplate);
    }

    private dispatchTaxonomies(annualReport: IAnnualReport): void {
        if (annualReport.annualReportTemplate == null) {
            // Getting Default Balance Template by LF & AB
            this.balanceTemplate$ = this.templatesProvider
                .getTemplateByLegalFormAndAccountBasis$()
                .pipe(
                    find((template: ITemplateEntityDTO) => template != null && template.type === TemplateType.BALANCE),
                );

            // Getting Default Income Template by LF & AB
            this.incomeTemplate$ = this.templatesProvider
                .getTemplateByLegalFormAndAccountBasis$()
                .pipe(
                    find((template: ITemplateEntityDTO) => template != null && template.type === TemplateType.INCOME),
                );

            // Dispatching getting default templates by accounting basis and legal form to get profitOfTheYear fli
            this.templatesProvider.dispatchTemplateByLegalFormAndAccountBasis(
                FinancialStatementTypes.BALANCE,
                annualReport.generalInformation.company.legalform?.name,
                annualReport.generalInformation.accountingBasis?.name,
            );
            this.templatesProvider.dispatchTemplateByLegalFormAndAccountBasis(
                FinancialStatementTypes.INCOME,
                annualReport.generalInformation.company.legalform?.name,
                annualReport.generalInformation.accountingBasis?.name,
            );
            return;
        }

        if (annualReport.annualReportTemplate.balanceTaxonomyId == null) {
            // Getting Default Balance Template by LF & AB
            this.balanceTemplate$ = this.templatesProvider
                .getTemplateByLegalFormAndAccountBasis$()
                .pipe(
                    find((template: ITemplateEntityDTO) => template != null && template.type === TemplateType.BALANCE),
                );

            // Dispatching getting default balance template by accounting basis and legal form to get profitOfTheYear fli
            this.templatesProvider.dispatchTemplateByLegalFormAndAccountBasis(
                FinancialStatementTypes.BALANCE,
                annualReport.generalInformation.company.legalform?.name,
                annualReport.generalInformation.accountingBasis?.name,
            );
        } else {
            // Getting Balance Taxonomy used in Ar Template
            this.balanceTemplate$ = this.actionsListener$.pipe(
                ofType(getTemplateById.success),
                filter((response) => response.payload.type === TemplateType.BALANCE),

                map((response) => response.payload),
            );

            this.templatesProvider.dispatchTemplateById(annualReport.annualReportTemplate.balanceTaxonomyId);
        }

        if (annualReport.annualReportTemplate.incomeTaxonomyId == null) {
            // Dispatching getting default templates by accounting basis and legal form to get profitOfTheYear fli
            this.templatesProvider.dispatchTemplateByLegalFormAndAccountBasis(
                FinancialStatementTypes.BALANCE,
                annualReport.generalInformation.company.legalform?.name,
                annualReport.generalInformation.accountingBasis?.name,
            );

            // Dispatching getting default balance template by accounting basis and legal form to get profitOfTheYear fli
            this.templatesProvider.dispatchTemplateByLegalFormAndAccountBasis(
                FinancialStatementTypes.INCOME,
                annualReport.generalInformation.company.legalform?.name,
                annualReport.generalInformation.accountingBasis?.name,
            );
        } else {
            // Getting Income Taxonomy used in Ar Template
            this.incomeTemplate$ = this.actionsListener$.pipe(
                ofType(getTemplateById.success),
                filter((response) => response.payload.type === TemplateType.INCOME),
                map((response) => response.payload),
            );

            this.templatesProvider.dispatchTemplateById(annualReport.annualReportTemplate.incomeTaxonomyId);
        }
    }

    private handlePreviousAnnualReport([previousAnnualReport, previousFinStatements]: [
        IAnnualReport,
        IFinStatements[],
    ]): void {
        this.previousAnnualReportCashFlowStatement =
            previousAnnualReport.cashFlowStatement != null ? previousAnnualReport.cashFlowStatement : undefined;

        // setting Flis
        const balanceTemplate = previousFinStatements[this.balanceIndex].templateAndAccountsMapping.template;
        const balanceFlis = this.cashFlowStatementMapper.getTemplateFlis(balanceTemplate);

        // getting previous annual profit fli

        let previousProfitFli: ICashFlowStatementEntry | undefined;
        if (this.previousAnnualReportCashFlowStatement) {
            previousProfitFli = this.previousAnnualReportCashFlowStatement.operatingActivities.entries.find(
                (entry) => entry.fliId === this.profitOfTheYearFli?.name,
            );

            if (previousProfitFli != null && this.profitOfTheYearFli != null) {
                this.profitOfTheYearFli.caption = previousProfitFli.name;
            }

            this.totalCaptions = {
                total1: this.previousAnnualReportCashFlowStatement.total.total1,
                total2: this.previousAnnualReportCashFlowStatement.total.total2,
            };

            if (this.currentAnnualReport?.cashFlowStatement == null) {
                this.operatingActivitiesTitle = this.previousAnnualReportCashFlowStatement.operatingActivities.title;
                this.financingActivitiesTitle = this.previousAnnualReportCashFlowStatement.financingActivities.title;
                this.investingActivitiesTitle = this.previousAnnualReportCashFlowStatement.investingActivities.title;
            }
        }

        // setting previous annual profit - a manual override is what the user saw and saved in the
        // previous year's cash flow step, so it wins over the calculated value. Same precedence as
        // CashFlowStatementHelper.prepareCashFlowFlisFromPreviousCashFlowStatement applies to every
        // other row, so the annual profit row is not seeded differently from the rest of the table.
        this.previousAnnualProfit = this.getPreviousYearValueOfEntry(previousProfitFli);

        // setting previous closing cash and cash equivalents
        if (
            previousAnnualReport.cashFlowStatement?.total.closingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(previousAnnualReport.cashFlowStatement.total.closingCashAndCashEquivalents)
        ) {
            this.previousClosingCashAndCashEquivalents = NumberUtils.roundNumber(
                previousAnnualReport.cashFlowStatement.total.closingCashAndCashEquivalents,
            );
        }

        // setting previous opening cash and cash equivalents
        if (
            previousAnnualReport.cashFlowStatement?.total.openingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(previousAnnualReport.cashFlowStatement.total.openingCashAndCashEquivalents)
        ) {
            this.previousOpeningCashAndCashEquivalents = NumberUtils.roundNumber(
                previousAnnualReport.cashFlowStatement.total.openingCashAndCashEquivalents,
            );
        }

        // mapping cash flow flis
        this.mapCashFlowFlis(true);
    }

    /**
     * The value a previous year cash flow entry contributes as this year's previous year value:
     * the user's manual override if there is one, otherwise the calculated value.
     */
    private getPreviousYearValueOfEntry(previousEntry: ICashFlowStatementEntry | undefined): number | undefined {
        if (previousEntry == null) {
            return undefined;
        }

        if (
            previousEntry.manuallyEnteredCurrentValue != null &&
            NumberUtils.isFinite(previousEntry.manuallyEnteredCurrentValue)
        ) {
            return NumberUtils.roundNumber(previousEntry.manuallyEnteredCurrentValue);
        }

        if (previousEntry.calculatedCurrentValue != null && NumberUtils.isFinite(previousEntry.calculatedCurrentValue)) {
            return NumberUtils.roundNumber(previousEntry.calculatedCurrentValue);
        }

        return undefined;
    }

    private handleTemplates(balanceTemplate: ITemplateEntityDTO, incomeTemplate: ITemplateEntityDTO): void {
        this.handleBalanceTemplate(balanceTemplate);

        this.handleIncomeTemplate(incomeTemplate);

        // mapping cash flow flis
        this.mapCashFlowFlis(false);
    }

    private handleBalanceTemplate(balanceTemplate: ITemplateEntityDTO): void {
        // setting Profit of the year fli
        this.profitOfTheYearFli = this.balanceFlis.find((fli) => fli.name === balanceTemplate.profitOfTheYear);

        // setting cash and cash equivalents fli
        this.cashAndCashEquivalentsFli = this.balanceFlis.find((fli) => fli.name === balanceTemplate.cashAndCash);

        // setting cashFlowDictionary
        this.flisCashFlowDictionary = CashFlowStatementHelper.prepareFliCashFlowDictionary(balanceTemplate);
    }

    private handleIncomeTemplate(incomeTemplate: ITemplateEntityDTO): void {
        // appending to cashFlowDictionary
        this.flisCashFlowDictionary = {
            ...this.flisCashFlowDictionary,
            ...CashFlowStatementHelper.prepareFliCashFlowDictionary(incomeTemplate),
        };
        // filtering income flis to only get the ones of type operations
        this.filterIncomeFlis();
    }

    private mapCashFlowFlis(fromPreviousYear: boolean): void {
        this.mappedFlis = [];
        let allFlis: IAccountMappingTemplateGroupItemFLI[] = [];
        if (this.incomeFlis.length !== 0) {
            allFlis = cloneDeep(this.balanceFlis.concat(this.incomeFlis));
        } else {
            allFlis = cloneDeep(this.balanceFlis);
        }
        if (allFlis.length !== 0) {
            if (this.previousAnnualReportCashFlowStatement != null) {
                this.autofillApplied = true;
                const preparedCashFlowFlis = CashFlowStatementHelper.prepareCashFlowFlisFromPreviousCashFlowStatement(
                    this.previousAnnualReportCashFlowStatement,
                    allFlis,
                    this.flisCashFlowDictionary,
                );

                this.mappedFlis = preparedCashFlowFlis;
            } else if (this.previousAnnualReportCashFlowStatement == null && fromPreviousYear) {
                //skip because it's not requred
            } else {
                this.autofillApplied = false;
                allFlis.forEach((fli) => {
                    const preparedCashFlowFli: ICashFlowFLI = CashFlowStatementHelper.prepareCashFlowFli(
                        fli,
                        this.flisCashFlowDictionary,
                    );
                    this.mappedFlis.push(preparedCashFlowFli);
                });
            }
        }
    }

    getCashFlowStatementOutput(): ICashFlowStatement | null {
        if (this.cashFlowStatementRef != null) {
            const updatedVersion = this.isFcpActive
                ? this.currentAnnualReport.fcp.version
                : this.currentAnnualReport.finStatements[this.balanceIndex].version;
            const version = updatedVersion;
            return this.cashFlowStatementRef.getCashFlowStatementOutput(version);
        } else {
            return null;
        }
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.CASH_FLOW_TIME_SPENT_TILL_COMPLETION);

        this.isSaveRequestInProgress = true;
        const cashFlowToBeSaved = this.getCashFlowStatementOutput();
        this.currentAnnualReport.cashFlowStatement = cashFlowToBeSaved;
        this.isSavedClicked = true;
        this.dispatchSaveAnnualReport(this.currentAnnualReport);
        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        if (this.isSaveRequestInProgress) {
            this.isSaveRequestInProgress = false;
            this.currentAnnualReportCashFlowStatement = cloneDeep<ICashFlowStatement>(
                this.getCashFlowStatementOutput(),
            );
            this.annualReportProvider.dispatchActiveAnnualReport(this.activeAnnualReportEncryptedId);
            this.savedSuccessfully.next(true);
            this.fireSuccessNotification();
        }
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (
            this.currentAnnualReport &&
            this.currentAnnualReport.cashFlowStatement == null &&
            this.disableProceedButton === false
        ) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            return of(this.isCashFlowStatementChanged());
        }
    }

    onValidityChange(isValid: boolean) {
        this.disableProceedButton = !isValid;
        this.disableSaveButton = !isValid;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !isValid;
    }

    onCurrentCashflowStatementTotalChange(currentTotal: number) {
        this.currentCashFlowStatementTotal = currentTotal;
        const latestCashFlowStatement = this.getCashFlowStatementOutput();

        const { closingEqualityCheck } = EqualityChecksHelper.calculateCashFlowStatementTotal(
            latestCashFlowStatement,
            this.currentCashFlowStatementTotal,
            this.previousCashFlowStatementTotal,
        );

        this.cashflowClosingDifference = closingEqualityCheck;
    }

    onPreviousCashflowStatementTotalChange(previousTotal: number) {
        this.previousCashFlowStatementTotal = previousTotal;
        const latestCashFlowStatement = this.getCashFlowStatementOutput();

        const { openingEqualityCheck } = EqualityChecksHelper.calculateCashFlowStatementTotal(
            latestCashFlowStatement,
            this.currentCashFlowStatementTotal,
            this.previousCashFlowStatementTotal,
        );

        this.cashflowOpeningDifference = openingEqualityCheck;
    }

    isCashFlowStatementChanged(): boolean {
        if (this.cashFlowStatementRef != null) {
            return this.cashFlowStatementRef.isCashFlowStatementChanged();
        } else {
            return false;
        }
    }

    protected override isDataConsistent(): Observable<ConsistencyCheckResult> {
        const latestCashFlowStatement = this.getCashFlowStatementOutput();
        if (this.currentCashFlowStatementTotal == null || this.previousCashFlowStatementTotal == null) {
            return of({
                isDataConsistent: true,
                warnings: ['Cash flow statement totals are undefined'],
            });
        }

        let cashFlowStatementTotalsEqualityCheckResult: EqualityChecksResult;
        try {
            cashFlowStatementTotalsEqualityCheckResult = EqualityChecksHelper.checkCashFlowStatementTotalsAreEqual(
                latestCashFlowStatement,
                this.currentCashFlowStatementTotal,
                this.previousCashFlowStatementTotal,
            );
        } catch (error) {
            cashFlowStatementTotalsEqualityCheckResult = EqualityChecksResult.BOTH_NOT_EQUAL;
            console.error('Error while checking cash flow statement totals equality:', error);
        }

        const isDataInconsistent = cashFlowStatementTotalsEqualityCheckResult !== EqualityChecksResult.BOTH_EQUAL;
        const warnings = this.getEqualityCheckDialogLocalizationKeys(cashFlowStatementTotalsEqualityCheckResult);

        return of({
            isDataConsistent: !isDataInconsistent,
            warnings: warnings,
        });
    }

    private getEqualityCheckDialogLocalizationKeys(
        cashFlowStatementTotalsEqualityCheckResult: EqualityChecksResult,
    ): string[] {
        switch (cashFlowStatementTotalsEqualityCheckResult) {
            case EqualityChecksResult.BOTH_NOT_EQUAL:
                return ['aumico_reports_equality_check_dialog_cashflow_totals_opening_and_closing_not_equal'];
            case EqualityChecksResult.CLOSING_NOT_EQUAL:
                return ['aumico_reports_equality_check_dialog_cashflow_totals_closing_not_equal'];
            case EqualityChecksResult.OPENING_NOT_EQUAL:
                return ['aumico_reports_equality_check_dialog_cashflow_totals_opening_not_equal'];
            case EqualityChecksResult.BOTH_EQUAL:
                return [];
        }
    }

    private filterIncomeFlis(): void {
        this.incomeFlis = this.incomeFlis.filter((fli) => {
            const fliCashFlowType = fli.cashFlowType ?? this.flisCashFlowDictionary[fli.name];
            return +fliCashFlowType === FinancialLineItemCashFlowType.OPERATIONS;
        });
    }

    public onDifferenceInTotal(previousTotal: number, currentTotal: number): void {
        this.cashflowOpeningDifference = NumberUtils.subtractNumbers(
            this.previousCashFlowStatementTotal,
            previousTotal,
        );

        this.cashflowClosingDifference = NumberUtils.subtractNumbers(this.currentCashFlowStatementTotal, currentTotal);
    }

    private updateColumnHeight(): void {
        const FOOTER_HEIGHT = 64;
        const col: HTMLElement | null = this.el.nativeElement.querySelector('.column-height-cfs');
        if (!col) return;
        const top = col.getBoundingClientRect().top;
        if (top <= 0) return; // element not yet laid out — skip until ResizeObserver fires again
        const height = window.innerHeight - top - FOOTER_HEIGHT - 12;
        this.el.nativeElement.style.setProperty('--scroll-table-height-cfs', `${height}px`);
    }
}
